import type { Achievement } from '@keel/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useTRPC } from '@/api/query';
import { achievementCopy } from '@/copy/engagement';
import { celebrate } from '@/lib/celebrate';
import { useAppStore } from '@/lib/store';
import { achievementIcon } from './icons';

/** Shows one achievement as a toast (no-op in Calm mode — the gallery still lists it). */
export const showAchievementToast = (a: Pick<Achievement, 'title' | 'description' | 'icon'>): void => {
  if (useAppStore.getState().calmMode) return;
  const Icon = achievementIcon(a.icon);
  toast(achievementCopy.unlocked(a.title), {
    description: a.description,
    icon: <Icon className="size-5 text-accent" aria-hidden />,
    duration: 7000,
    // lazy import: the router module imports this one (via the app shell)
    action: { label: 'See journey', onClick: () => void import('@/routes').then((m) => m.router.navigate('/journey')) },
  });
};

const SHOWN_KEY = 'keel:achievements-shown';
const shownKeys = (): Set<string> => {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(SHOWN_KEY) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
};

type Announceable = Pick<Achievement, 'title' | 'description' | 'icon'> & Partial<Pick<Achievement, 'key' | 'entityId'>>;

/**
 * Toast a batch of freshly unlocked achievements (e.g. from `engagement.checkIn`) with a small celebration. Several places
 * can report the same unlock (check-in result, Home summary, the watcher), so each achievement toasts once per session.
 */
export const announceAchievements = (list: readonly Announceable[] | undefined): void => {
  if (!list?.length) return;
  const seen = shownKeys();
  const fresh = list.filter((a) => {
    const k = `${a.entityId ?? ''}:${a.key ?? a.title}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  try {
    sessionStorage.setItem(SHOWN_KEY, JSON.stringify([...seen]));
  } catch {
    // private mode: in-memory dedupe for this call only
  }
  if (!fresh.length) return;
  fresh.forEach(showAchievementToast);
  celebrate('small');
};

/**
 * Watches `engagement.achievements` for unseen items, toasts them once and marks them seen.
 * Mount once inside the authenticated shell.
 */
export const AchievementWatcher = () => {
  const trpc = useTRPC();
  const shown = useRef(new Set<string>());
  const q = useQuery(trpc.engagement.achievements.queryOptions(undefined, { meta: { silent: true }, staleTime: 60_000 }));
  const markSeen = useMutation(trpc.engagement.markAchievementsSeen.mutationOptions({ meta: { silent: true } }));
  const achievements: Achievement[] | undefined = q.data;
  const { mutate } = markSeen;

  useEffect(() => {
    const unseen = (achievements ?? []).filter((a) => !a.seenAt && !shown.current.has(a.id));
    if (!unseen.length) return;
    unseen.forEach((a) => shown.current.add(a.id));
    // one thing at a time: wait for a first-run tour (driver.js marks <body>) to finish before celebrating
    let timer: ReturnType<typeof setTimeout>;
    const announce = () => {
      if (document.body.classList.contains('driver-active')) timer = setTimeout(announce, 1500);
      else announceAchievements(unseen);
    };
    timer = setTimeout(announce, 1200);
    mutate({ ids: unseen.map((a) => a.id) });
    return () => clearTimeout(timer);
  }, [achievements, mutate]);

  return null;
};
